// ==================================
// Pagina Lista de Veterinarias
// ==================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import {
  Button,
  Input,
  Select,
  Table,
  Badge,
  Card,
  Pagination,
} from '@/componentes/ui';
import { veterinariasServicio } from '@/servicios';
import type { Veterinaria, EstadoLicencia, TipoLicencia } from '@/tipos';

// Filtro de estado de la VETERINARIA (activo/inactivo)
const estadoVeterinariaOptions = [
  { value: '', label: 'Todas' },
  { value: 'activo', label: 'Activas' },
  { value: 'inactivo', label: 'Inactivas' },
];

// Filtro de estado de la LICENCIA
const estadoLicenciaOptions = [
  { value: '', label: 'Todas las licencias' },
  { value: 'activa', label: 'Activa' },
  { value: 'en_gracia', label: 'En gracia' },
  { value: 'expirada', label: 'Expirada' },
  { value: 'suspendida', label: 'Suspendida' },
  { value: 'pendiente', label: 'Pendiente' },
];

// Filtro de tipo de licencia
const tipoLicenciaOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'gratis', label: 'Gratis' },
  { value: 'prueba', label: 'Prueba' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'anual', label: 'Anual' },
];

function getBadgeVariant(
  estado: EstadoLicencia
): 'success' | 'warning' | 'danger' | 'info' | 'gray' {
  const variants: Record<
    EstadoLicencia,
    'success' | 'warning' | 'danger' | 'info' | 'gray'
  > = {
    activa: 'success',
    en_gracia: 'warning',
    expirada: 'danger',
    suspendida: 'danger',
    pendiente: 'gray',
  };
  return variants[estado] || 'gray';
}

function getTipoLabel(tipo: TipoLicencia): string {
  const labels: Record<TipoLicencia, string> = {
    gratis: 'Gratis',
    prueba: 'Prueba',
    mensual: 'Mensual',
    anual: 'Anual',
  };
  return labels[tipo] || tipo;
}

export function Veterinarias() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busqueda, setBusqueda] = useState('');

  const [estadoVeterinaria, setEstadoVeterinaria] = useState('');
  const [estadoLicencia, setEstadoLicencia] = useState('');
  const [tipoLicencia, setTipoLicencia] = useState('');
  const [pagina, setPagina] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['veterinarias', busqueda, estadoVeterinaria, estadoLicencia, tipoLicencia, pagina],
    queryFn: () =>
      veterinariasServicio.listar({
        busqueda: busqueda || undefined,
        estado: estadoVeterinaria || undefined,
        estadoLicencia: estadoLicencia || undefined,
        tipoLicencia: tipoLicencia || undefined,
        pagina,
        limite: 10,
      }),
  });

  const toggleActivoMutation = useMutation({
    mutationFn: (vet: Veterinaria) =>
      vet.activo ? veterinariasServicio.desactivar(vet.id) : veterinariasServicio.activar(vet.id),
    onSuccess: (_, vet) => {
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      toast.success(vet.activo ? 'Veterinaria desactivada' : 'Veterinaria activada');
    },
    onError: () => toast.error('Error al cambiar el estado'),
  });

  const handleToggleActivo = (vet: Veterinaria, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleActivoMutation.mutate(vet);
  };

  const handleEditClick = (vet: Veterinaria, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/veterinarias/${vet.id}/editar`);
  };

  const columns = [
    {
      key: 'nombre',
      header: 'Veterinaria',
      render: (vet: Veterinaria) => (
        <div>
          <p className="font-medium text-obsidian-900">{vet.nombre}</p>
          <p className="text-sm text-stone-500">{vet.slug}.alebrijecloud.com</p>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Contacto',
      render: (vet: Veterinaria) => (
        <div>
          <p className="text-obsidian-900">{vet.email}</p>
          <p className="text-sm text-stone-500">{vet.telefono}</p>
        </div>
      ),
    },
    {
      key: 'licencia',
      header: 'Licencia',
      render: (vet: Veterinaria) => (
        <div className="space-y-1">
          <Badge variant={getBadgeVariant(vet.licencia.estado)}>
            {vet.licencia.estado.replace('_', ' ')}
          </Badge>
          <p className="text-xs text-stone-500">
            {getTipoLabel(vet.licencia.tipo)}
          </p>
        </div>
      ),
    },
    {
      key: 'kitPagado',
      header: 'Kit',
      render: (vet: Veterinaria) => (
        <Badge variant={vet.kitPagado ? 'success' : 'warning'}>
          {vet.kitPagado ? 'Pagado' : 'Pendiente'}
        </Badge>
      ),
    },
    {
      key: 'activo',
      header: 'Estado',
      render: (vet: Veterinaria) => (
        <Badge variant={vet.activo ? 'success' : 'gray'}>
          {vet.activo ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Registro',
      render: (vet: Veterinaria) => (
        <span className="text-stone-500">
          {new Date(vet.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (vet: Veterinaria) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => handleEditClick(vet, e)}
            className="p-1.5 text-stone-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Editar"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => handleToggleActivo(vet, e)}
            className={`p-1.5 rounded-lg transition-colors ${
              vet.activo
                ? 'text-stone-500 hover:text-amber-600 hover:bg-amber-50'
                : 'text-stone-500 hover:text-green-600 hover:bg-green-50'
            }`}
            title={vet.activo ? 'Desactivar' : 'Activar'}
          >
            {vet.activo ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header titulo="Veterinarias" />

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Filtros */}
        <Card>
          <div className="space-y-4">
            {/* Fila 1: Busqueda y boton */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por nombre, email o slug..."
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value);
                    setPagina(1);
                  }}
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
                />
              </div>
              <Button
                variant="primary"
                onClick={() => navigate('/veterinarias/nueva')}
                className="w-full sm:w-auto whitespace-nowrap"
                leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
              >
                Nueva Veterinaria
              </Button>
            </div>

            {/* Fila 2: Filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select
                options={estadoVeterinariaOptions}
                value={estadoVeterinaria}
                onChange={(e) => {
                  setEstadoVeterinaria(e.target.value);
                  setPagina(1);
                }}
              />
              <Select
                options={estadoLicenciaOptions}
                value={estadoLicencia}
                onChange={(e) => {
                  setEstadoLicencia(e.target.value);
                  setPagina(1);
                }}
              />
              <Select
                options={tipoLicenciaOptions}
                value={tipoLicencia}
                onChange={(e) => {
                  setTipoLicencia(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>
        </Card>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            data={data?.data || []}
            keyExtractor={(vet) => vet.id}
            loading={isLoading}
            emptyMessage="No se encontraron veterinarias"
            onRowClick={(vet) => navigate(`/veterinarias/${vet.id}`)}
          />
        </div>

        {/* Paginacion */}
        {data?.paginacion && (
          <Pagination
            pagina={data.paginacion.pagina}
            totalPaginas={data.paginacion.totalPaginas}
            onCambiarPagina={setPagina}
          />
        )}
      </div>

    </div>
  );
}

export default Veterinarias;
